<script lang="ts">
	import DetailPageLayout from "@components/DetailPageLayout.svelte";
	import Logo from "@components/Logo.svelte";
	import PreloadedPrice from "@components/PreloadedPrice.svelte";
	import ProductTags from "@components/ProductTags.svelte";
	import PromoBanner from "@components/PromoBanner.svelte";
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import Carousel from "@components/common/Carousel.svelte";
	import CkEditorContent from "@components/common/CkEditorContent.svelte";
	import Disclosure from "@components/common/Disclosure.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Media from "@components/common/Media.svelte";
	import PriceDisplay from "@components/common/PriceDisplay.svelte";
	import { getMediaSourceFromProductGallery } from "@components/getMediaSourceFromProductGallery.js";
	import { getTagsForProductCard } from "@components/getTagsForProductCard.js";
	import Layout from "@components/layout/Layout.svelte";
	import ProductBasketButtons from "@components/product/ProductBasketButtons.svelte";
	import ProductStructuredData from "@components/product/ProductStructuredData.svelte";
	import { calculateDeliveryETA } from "@components/product/calculateDeliveryETA.js";
	import ProductSection from "@components/sections/ProductSection.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import { onMount } from "svelte";
	import info from "@core/assets/icons/info.svg?raw";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import type { Country } from "@core/schema/Country.js";
	import type { Currency } from "@core/schema/Currency.js";
	import { CustomerType } from "@core/schema/CustomerType.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import { Locale } from "@core/schema/Locale.js";
	import type { ProductAttribute, ProductAttributeGroup, ProductVariantForCustomer } from "@core/schema/Product.js";
	import { ProductAvailability } from "@core/schema/ProductAvailability.js";
	import type { ProductGallery } from "@core/schema/ProductGallery.js";
	import { ProductVariantType } from "@core/schema/ProductVariantType.js";
	import { ProductVisibilityScope } from "@core/schema/ProductVisibilityScope.js";
	import { SubscriptionKind } from "@core/schema/SubscriptionKind.js";
	import { SubscriptionSelection } from "@core/schema/SubscriptionSelection.js";
	import { SubscriptionVariant } from "@core/schema/SubscriptionVariant.js";
	import { productAttributeGroupTypeNameMap } from "@core/schema/names/productAttributeGroupTypeNameMap.js";
	import { getMembershipPagePath } from "@core/schema/paths/getMembershipPagePath.js";
	import { getSubscriptionPagePath } from "@core/schema/paths/getSubscriptionPagePath.js";
	import { getPriceForCustomerOfProductVariant } from "@core/schema/utils/getPriceForCustomerOfProductVariant.js";
	import { getProductAttributeGroupsOfValues } from "@core/schema/utils/getProductAttributeGroupsOfValues.js";
	import { getProductAvailability } from "@core/schema/utils/getProductAvailability.js";
	import { getProductOfVariant } from "@core/schema/utils/getProductOfVariant.js";
	import { getProductPrimaryMedia } from "@core/schema/utils/getProductPrimaryMedia.js";
	import { getProductStockAmount } from "@core/schema/utils/getProductStockAmount.js";
	import { makeInflect } from "@core/utils/makeInflect.js";
	import { oneTimeEffect } from "@core/utils/svelte/oneTimeEffect.svelte.js";
	import { toDateMonthYearFormat } from "@core/utils/toDateMonthYearFormat.js";
	import { reportProductDetail } from "../UniversalAnalytics.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { CartStep } from "../cart/CartStep.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { buildProductAlt } from "../seo/buildProductAlt.js";
	import { selectedCurrency } from "../state/selectedCurrency.svelte.js";
	import { selectedDestinationCountry } from "../state/selectedDestinationCountry.svelte.js";
	import translations from "./ProductDetail.translations.json" with { type: "json" };
	import type { ProductDetailProps } from "./ProductDetailProps.js";

	const { product, subcategory, relatedProducts }: ProductDetailProps = $props();
	const { tenant, subcategories, cafeinaProduct, mothersBlendProduct } = getGlobalTemplateProps();

	const isCafeinaProduct = $derived(product.id === cafeinaProduct.id);
	const isMothersBlendProduct = $derived(product.id === mothersBlendProduct.id);
	const showSubscriptionOffer = $derived(
		(isCafeinaProduct || isMothersBlendProduct) && Auth.customerType === CustomerType.B2c,
	);

	const translate = getTranslate(translations);

	const [defaultVariant] = $derived(product.variants);
	const defaultAttributes = $derived.by(() => {
		const grouped: Record<ProductAttributeGroup["id"], ProductAttribute["id"]> = {};
		for (const group of product.attributeGroups) {
			const [first] = group.values;
			if (!first) {
				continue;
			}
			grouped[group.id] = first.id;
		}
		return grouped;
	});

	let descriptionExpanded = $state(false);
	let selectedVariant = $derived(defaultVariant?.id);
	let selectedAttributes = $derived(defaultAttributes);
	const deliveryDate = $derived(calculateDeliveryETA(new Date(), selectedDestinationCountry.current));
	const descriptionExpandAllowed = $derived(
		product.shortDescription[tenant.locale] !== product.description[tenant.locale],
	);

	const productVariant = $derived(
		selectedVariant !== undefined ? getProductOfVariant(product, selectedVariant) : undefined,
	);
	const productAttributes = $derived(getProductAttributeGroupsOfValues(product.attributeGroups, selectedAttributes));
	const requiresFfcMembership = $derived(
		productVariant?.variant.visibilityScope === ProductVisibilityScope.FfcMembersOnly && !Auth.isFfcMember,
	);

	onMount(() => {
		const searchParams = new URLSearchParams(window.location.search);
		const variant = searchParams.get("variant");
		if (variant !== null) {
			selectedVariant = variant;
		}
		for (const group of product.attributeGroups) {
			const value = searchParams.get(`attribute-${group.id}`);
			if (value !== null) {
				selectedAttributes = {
					...selectedAttributes,
					[group.id]: value,
				};
			}
		}
		if (!selectedVariant) {
			throw new Error(`No variant selected for product: ${product.id}`);
		}
	});

	oneTimeEffect(
		() =>
			selectedCurrency.current !== undefined && selectedDestinationCountry.current !== null && Boolean(selectedVariant),
		() => {
			if (selectedCurrency.current === undefined || selectedDestinationCountry.current === null || !selectedVariant) {
				return;
			}
			const productVariant = getProductOfVariant(product, selectedVariant);
			const productAttributes = getProductAttributeGroupsOfValues(product.attributeGroups, selectedAttributes);
			reportProductDetail(tenant.locale, productVariant, productAttributes, {
				currency: selectedCurrency.current,
				destinationCountry: selectedDestinationCountry.current,
				subcategories,
				vatType: EconomicSubjectVatType.NotVatSubject,
			});
		},
	);

	function selectVariant(id: ProductVariantForCustomer["id"]): void {
		selectedVariant = id;
		const url = new URL(window.location.href);
		if (defaultVariant?.id === id) {
			url.searchParams.delete("variant");
		} else {
			url.searchParams.set("variant", id);
		}
		window.history.replaceState({}, "", url);
	}

	function selectAttribute(group: ProductAttributeGroup, value: ProductAttribute): void {
		selectedAttributes = {
			...selectedAttributes,
			[group.id]: value.id,
		};
		const url = new URL(window.location.href);
		if (defaultAttributes[group.id] === value.id) {
			url.searchParams.delete(`attribute-${group.id}`);
		} else {
			url.searchParams.set(`attribute-${group.id}`, value.id);
		}
		window.history.replaceState({}, "", url);
	}

	const selectedCombinationStock = $derived.by(() => {
		const variant = productVariant?.variant ?? defaultVariant;
		if (variant === undefined) {
			return Infinity;
		}

		return getProductStockAmount(product.stock, variant, productAttributes);
	});

	const combinationAvailability = $derived(
		getProductAvailability(selectedCombinationStock, product.shippingStartsAt, Date.now()),
	);
	const preorderShippingStartsAt = $derived(
		combinationAvailability === ProductAvailability.PreOrder ? product.shippingStartsAt : undefined,
	);

	const primaryMedia = $derived.by(() => {
		if (!productVariant) {
			return undefined;
		}

		return getProductPrimaryMedia(productVariant, productAttributes);
	});

	const gallery = $derived.by((): ProductGallery[] => {
		if (!primaryMedia) {
			return product.gallery;
		}

		return [
			{
				id: crypto.randomUUID(),
				photo: primaryMedia,
			},
			...product.gallery,
		];
	});

	const piecesInflect = {
		[Locale.cs]: makeInflect("kus", "kusy", "kusů"),
		[Locale.en]: makeInflect("piece", "pieces", "pieces"),
	}[tenant.locale];

	const galleryFallbackAlt = $derived(
		buildProductAlt(product, subcategory, tenant.locale, productAttributes, productVariant?.variant),
	);
</script>

<ProductStructuredData {product} {subcategory} />

<Layout pageHasMobileBottomBar ogImage={primaryMedia}>
	<DetailPageLayout title={product.name[tenant.locale]}>
		{#snippet content()}
			<div class="text-base/normal !text-fcrgray">
				<CkEditorContent
					html={descriptionExpanded ? product.description[tenant.locale] : product.shortDescription[tenant.locale]}
					class="text-sm/normal sm:text-base"
				/>

				{#if descriptionExpandAllowed}
					<button
						type="button"
						class="underline inline-block mt-4"
						onclick={(): void => {
							descriptionExpanded = !descriptionExpanded;
						}}
					>
						<Formatted text={translate(descriptionExpanded ? "showLess" : "readMore")} />
					</button>
				{/if}
			</div>
			{#if product.variantType !== ProductVariantType.None}
				<div class="flex items-center gap-2 overflow-x-auto text-nowrap p-4 -my-4">
					{#each product.variants as variant (variant.id)}
						<Button
							type="button"
							variant={ButtonVariant.Outlined}
							size={ButtonSize.Medium}
							class={[
								selectedVariant === variant.id &&
									"outline-2 outline-fcrdarkgray !cursor-default outline [&:not(:disabled):hover]:border-transparent",
								product.variants.length > 2 && "xl:w-full",
							]}
							onclick={(): void => {
								selectVariant(variant.id);
							}}
						>
							{variant.label[tenant.locale]}
						</Button>
					{/each}
				</div>
			{/if}
			{#each product.attributeGroups as group (group.id)}
				<div class="flex flex-col gap-2">
					<div class="text-fcrdarkgray text-5/tighter font-medium font-antipol">
						<Formatted text={productAttributeGroupTypeNameMap[group.type][tenant.locale]} />:
					</div>
					<!-- Enable this if when adding more attribute group types. Right now linter doesn't like it because the enum has only one value -->
					<!-- {#if attributeGroups.type === ProductAttributeGroupType.Color} -->
					<div class="flex items-center gap-2">
						{#each group.values as value (value.id)}
							<div
								class={[
									"size-9 rounded-full flex items-center justify-center",
									selectedAttributes[group.id] === value.id
										? "outline outline-fcrdarkgray"
										: "hover:outline hover:outline-fcrlightgray",
								]}
							>
								<button
									class={["size-8 rounded-full border border-fcrgray/30"]}
									style={`background-color: ${value.value}`}
									title={value.label[tenant.locale]}
									onclick={(): void => {
										selectAttribute(group, value);
									}}
								></button>
							</div>
						{/each}
					</div>
					<!-- {/if} -->
				</div>
			{/each}
			{#if productVariant}
				<PreloadedPrice class="h-12">
					{#snippet children(currency: Currency, destinationCountry: Country | "outsideEu")}
						{@const { withTax, withoutTax, taxRate } = getPriceForCustomerOfProductVariant(
							productVariant,
							currency,
							destinationCountry,
							EconomicSubjectVatType.NotVatSubject,
						)}
						<PriceDisplay
							{withTax}
							{withoutTax}
							{taxRate}
							{currency}
							locale={tenant.locale}
							withoutTaxLabel={translate("withoutTax")}
						/>
					{/snippet}
				</PreloadedPrice>
			{/if}
		{/snippet}

		{#snippet form()}
			{#if productVariant}
				<ProductBasketButtons
					maxQuantity={selectedCombinationStock}
					product={productVariant}
					attributes={productAttributes}
					locked={requiresFfcMembership}
					class="fixed xl:static bottom-0 inset-x-0 bg-white z-10 p-4 xl:p-0"
				/>
			{/if}
		{/snippet}

		{#snippet availability()}
			<div class="flex flex-col gap-3">
				<div class="text-fcrgray text-4 flex items-center gap-2">
					{#if combinationAvailability === ProductAvailability.OutOfStock}
						<div class="size-4 bg-fcrerror rounded-full"></div>
						<div>
							<Formatted text={translate("soldOut")} />
						</div>
					{:else if preorderShippingStartsAt}
						<div class="size-4 bg-fcramber rounded-full"></div>
						<div>
							<Formatted
								text={translate("preorderShipping", {
									shippingDate: toDateMonthYearFormat(preorderShippingStartsAt, tenant.locale),
								})}
							/>
						</div>
					{:else}
						<div class="size-4 bg-fcrsuccess rounded-full"></div>
						<div>
							{#if deliveryDate}
								<Formatted
									text={translate("deliveredUntil", {
										deliveryDate: toDateMonthYearFormat(deliveryDate, tenant.locale),
									})}
								/>
							{:else}
								<Formatted text={translate("inStock")} />
							{/if}
						</div>
					{/if}
				</div>
				{#if product.amountInOnePackage !== undefined}
					<div class="text-fcrgray text-4 flex items-center gap-2">
						<Icon icon={info} class="size-4" />
						<div>
							<Formatted
								text={translate("amountInOnePackage", {
									amount: product.amountInOnePackage,
									unit: piecesInflect(product.amountInOnePackage),
								})}
							/>
						</div>
					</div>
				{/if}
			</div>
		{/snippet}

		{#snippet mobileGallery()}
			{#if gallery.length > 0}
				<Carousel items={gallery} options={{ loop: true, align: "start" }}>
					{#snippet item(media: ProductGallery, imageIndex: number, dupe: boolean)}
						<div class="h-70 relative px-1">
							<Media
								eager={imageIndex === 0}
								sourcesets={media.photo ? { ">xl": [0, 800], "<xl": [500, 0] } : {}}
								source={getMediaSourceFromProductGallery(media)}
								alt={media.photo?.title ?? galleryFallbackAlt}
								width={400}
								height={225}
								class="size-full object-cover"
								lightbox={!dupe}
								lightboxWidth={1280}
							/>
							{#if imageIndex === 0}
								<ProductTags
									class="absolute top-4 left-4"
									tags={getTagsForProductCard({ product, subcategory, locale: tenant.locale, includePreorder: false })}
								/>
							{/if}
						</div>
					{/snippet}
				</Carousel>
			{:else}
				<div class="h-50 px-1 bg-fcrlightgray-300 rounded">
					<Logo class="size-full p-5" />
				</div>
			{/if}
		{/snippet}

		{#snippet desktopGallery()}
			{#each gallery as media, index (media.id)}
				<div class="relative">
					<Media
						lightbox
						sourcesets={media.photo ? { ">xl": [0, 800], "<xl": [200, 0] } : {}}
						source={media.photo ?? { youtube: media.youtube ?? "" }}
						alt={media.photo?.title ?? galleryFallbackAlt}
						width={400}
						height={225}
						class="w-full"
					/>
					{#if index === 0}
						<ProductTags
							class="absolute top-4 left-4"
							tags={getTagsForProductCard({ product, subcategory, locale: tenant.locale, includePreorder: false })}
						/>
					{/if}
				</div>
			{:else}
				<div class="w-full relative pb-[56.25%]">
					<div class="absolute inset-0 p-20 bg-fcrlightgray-300 rounded">
						<Logo class="size-full" />
					</div>
				</div>
			{/each}
		{/snippet}

		{#snippet promo()}
			{#if requiresFfcMembership}
				<PromoBanner
					class="bg-fcrbrown"
					heading={translate("ffcMembersOnlyHeading")}
					label={translate("ffcMembersOnlyDescription")}
					action={{ href: getSubscriptionPagePath(tenant) }}
				/>
			{:else if !Auth.loggedCustomer}
				<PromoBanner
					class="bg-fcrbrown"
					heading={translate("betterOfferWithMembership")}
					label={translate("betterOfferWithMembershipDescription")}
					action={{ href: getMembershipPagePath(tenant) }}
				/>
			{:else if showSubscriptionOffer}
				<PromoBanner
					class="bg-fcrblue"
					heading={translate("betterOfferWithSubscription")}
					label={translate("betterOfferWithSubscriptionDescription")}
					action={(): void => {
						const cart = Cart.getCorrectCartInStepOrThrow(CartStep.PaymentDelivery);
						cart.subscriptionModalStep = 0;
						Cart.saveSubscriptionFields({
							selection: isCafeinaProduct ? SubscriptionSelection.Cafeina : SubscriptionSelection.MothersBlend,
							variant: SubscriptionVariant.Espresso,
						});
						Cart.openSubscriptionConfigurator(SubscriptionKind.Recurring);
					}}
				/>
			{/if}
		{/snippet}

		{#snippet disclosures()}
			{#each product.disclosures[tenant.locale] as disclosure, i (i)}
				<Disclosure title={disclosure.title}>
					<CkEditorContent html={disclosure.content} class="text-sm/normal sm:text-base !text-fcrgray" />
				</Disclosure>
			{/each}
		{/snippet}
	</DetailPageLayout>

	<ProductSection {subcategories} products={relatedProducts} title={translate("relatedProducts")} class="bg-fcrbeige" />
</Layout>
